
import { mergeMap, map, distinctUntilChanged, mapTo } from 'rxjs/operators'
import { EpicRefined } from 'interfaces/middleware'
import { isAuthenticatedSelector } from 'selectors/globalSelectors'
import { combineEpics, ActionsObservable } from 'redux-observable'
import { WsActions, WsMessageAction, WsOpenAction, WsSendAction } from 'actions/webSocketActions'
import { merge, of, EMPTY } from 'rxjs'
import { Action } from 'actions'
import { Store } from 'interfaces/state'
import { WSOutcomeMessage, WSIncomeMessage } from 'components/features/webSocket/types'
import { getInsightsPlacements } from 'helpers/insightHelpers'
import { SetInsights, setInsightsActionCreator } from 'actions/insightsActions'
import { normalize } from 'helpers/normalizeHelpers'
import { GetMixedAccounts, getMixedAccountsActionCreator, LinkageResult, linkageResultActionCreator } from 'actions/linkageActions'
import { UpdateNavigationCounters, updateNavigationCountersActionCreator } from 'actions/sidebarActions'
import { byType } from 'rxOperators/byType'
import { WebSocketConnector } from 'components/features/webSocket/WebSocketConnector'
import { isWsEnabled, socketUrl } from 'constants/webSocket'

type WSC = WebSocketConnector<WSIncomeMessage, WSOutcomeMessage>

const createAuthStream = (action$: ActionsObservable<Action>, store: Store) => {
  return action$.pipe(
    map(() => {
      const state = store.getState()
      return !!isAuthenticatedSelector(state)
    }),
    distinctUntilChanged()
  )
}


let currentConnector: WSC | null = null

const setupConnectorEpic: EpicRefined<WsActions> = (action$, store) => {
  if (!isWsEnabled()) {
    return EMPTY
  }

  return createAuthStream(action$, store).pipe(
    mergeMap((isAuth) => {
      if (currentConnector) {
        currentConnector.disconnect()
        currentConnector = null
      }

      if (!isAuth) {
        return EMPTY
      }

      currentConnector = new WebSocketConnector<WSIncomeMessage, WSOutcomeMessage>(socketUrl)

      return merge(
        currentConnector.event$.pipe(
          map((message) => new WsMessageAction(message))
        ),
        currentConnector.open$.pipe(mapTo(new WsOpenAction())),
      )
    }),
  )
}

const sendEpic: EpicRefined<WsActions> = (action$) => action$.pipe(
  byType(WsSendAction.type),
  mergeMap(({ payload }) => {
    if (currentConnector) {
      currentConnector.send(payload)
    }

    return EMPTY
  }),
)

const openEpic: EpicRefined<WsActions> = (action$) =>
  action$.pipe(
    byType(WsOpenAction.type),
    mergeMap(() => {
      return of(
        new WsSendAction({
          event: 'request_notifications_list',
          data: null,
        }),
        new WsSendAction({
          event: 'request_counters',
          data: null,
        }),
      )
    })
  )

/*
  DEPRECATED!
  Do not extend this epic!
  Handle your events in relative epics
*/
const backCompatibilityEpic: EpicRefined<WsActions | SetInsights | LinkageResult | GetMixedAccounts | UpdateNavigationCounters> = (action$) =>
  action$.pipe(
    byType(WsMessageAction.type),
    mergeMap(({ payload }) => {
      if (payload.event === 'notifications_list') {
        const placements = getInsightsPlacements(payload.data)
        const insightsCollection = normalize(payload.data, 'id')
        return of(setInsightsActionCreator(insightsCollection, placements, false))
      }

      if (payload.event === 'linkage_result') {
        return of(linkageResultActionCreator(payload.data))
      }

      if (payload.event === 'provider_accounts_changed') {
        return of(getMixedAccountsActionCreator.request({}))
      }

      if (payload.event === 'counters') {
        return of(updateNavigationCountersActionCreator(payload.data))
      }

      return EMPTY
    })
  )


export const webSocketEpic = combineEpics(
  setupConnectorEpic,
  sendEpic,
  openEpic,
  backCompatibilityEpic,
)
